// src/pages/Marketplace.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Search, X, List as ListIcon, Map as MapIcon, Image as ImageIcon, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FixedSizeList as VirtualList } from "react-window";
import useDebouncedValue from "../../lib/useDebouncedValue";

// --- Fix default Leaflet marker icon ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// --- Fit Bounds Component ---
function FitBounds({ markers, searchRadius }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !markers || markers.length === 0) return;

    const userMarker = markers.find((m) => m.isUser) || markers[0];
    let zoomLevel = 9;
    if (searchRadius <= 5) zoomLevel = 12;
    else if (searchRadius <= 15) zoomLevel = 11;
    else if (searchRadius <= 30) zoomLevel = 10;
    else if (searchRadius <= 40) zoomLevel = 9;
    else zoomLevel = 8;

    map.setView([userMarker.latitude, userMarker.longitude], zoomLevel, { animate: true });

    setTimeout(() => {
      map.invalidateSize();
    }, 200);

  }, [map, markers, searchRadius]);

  return null;
}

export default function Marketplace({ user }) {
  // --- Data State ---
  const [userLocation, setUserLocation] = useState([47.6062, -122.3321]); // Default Seattle
  const [listHeight, setListHeight] = useState(520);

  // --- Filter State ---
  const [sport, setSport] = useState("");
  const [age, setAge] = useState("");
  const [grade, setGrade] = useState("");
  const [programType, setProgramType] = useState("");
  const [registrationFormatFilter, setRegistrationFormatFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [distance, setDistance] = useState(50); 
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState({
    age: "",
    grade: "",
    registrationFormatFilter: "",
    genderFilter: "",
  });

  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSearchProgram, setSelectedSearchProgram] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // --- UI State ---
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(user?.id || null);
  const mapRef = useRef(null);
  const hasInitializedMapView = useRef(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 200);

  // --- Auth Listener ---
  useEffect(() => {
    if (user?.id) setCurrentUserId(user.id);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.id) setCurrentUserId(session.user.id);
      else setCurrentUserId(null);
    });

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) setCurrentUserId(session.user.id);
    };
    checkSession();

    return () => subscription.unsubscribe();
  }, [user]);

  const { data: programs = [] } = useQuery({
    queryKey: ["programs"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("programs")
        .select(
          "id, name, sport, program_type, registration_format, website_url, latitude, longitude, program_logo, age_groups, boys_ages_offered, girls_ages_offered"
        );
      if (error) throw error;
      return (data || []).map((prog) => {
        const rawPath = prog.program_logo;
        if (!rawPath) return { ...prog, logo_url: null };
        if (rawPath.startsWith("http")) return { ...prog, logo_url: rawPath };
        const { data: urlData } = supabase.storage.from("program_logos").getPublicUrl(rawPath);
        return { ...prog, logo_url: urlData?.publicUrl || null };
      });
    },
  });

  const { data: followData = [] } = useQuery({
    queryKey: ["followed-programs", currentUserId],
    enabled: Boolean(currentUserId),
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_program_follows")
        .select("program_id")
        .eq("user_id", currentUserId);
      if (error) throw error;
      return data || [];
    },
  });

  const followedPrograms = useMemo(
    () => (followData || []).map((d) => d.program_id),
    [followData]
  );

  const {
    data: userLocationRow,
    isLoading: userLocationLoading,
    isFetched: userLocationFetched,
  } = useQuery({
    queryKey: ["user-location", currentUserId],
    enabled: Boolean(currentUserId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("user_lat, user_lng")
        .eq("id", currentUserId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: athleteDemographics } = useQuery({
    queryKey: ["athlete-demographics", currentUserId],
    enabled: Boolean(currentUserId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("age, grade")
        .eq("id", currentUserId)
        .maybeSingle();
      if (error) throw error;
      return data || null;
    },
  });

  const programIds = useMemo(() => programs.map((p) => p.id), [programs]);

  const { data: tryouts = [] } = useQuery({
    queryKey: ["tryouts", programIds],
    enabled: programIds.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (programIds.length === 0) return [];
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 90);
      const startStr = start.toISOString().slice(0, 10);
      const endStr = end.toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("tryouts")
        .select("program_id, tryout_date")
        .in("program_id", programIds)
        .gte("tryout_date", startStr)
        .lte("tryout_date", endStr)
        .order("tryout_date", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const tryoutData = useMemo(() => {
    const programTryouts = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    (tryouts || []).forEach((t) => {
      if (!programTryouts[t.program_id]) {
        programTryouts[t.program_id] = { count: 0, nextDate: null };
      }
      programTryouts[t.program_id].count += 1;
      const tDate = new Date(t.tryout_date);
      if (!programTryouts[t.program_id].nextDate && tDate >= today) {
        programTryouts[t.program_id].nextDate = t.tryout_date;
      }
    });
    return programTryouts;
  }, [tryouts]);

  useEffect(() => {
    const fallbackToSystemLocation = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setUserLocation([position.coords.latitude, position.coords.longitude]),
        () => console.warn("Geolocation not available")
      );
    };

    if (!currentUserId) {
      fallbackToSystemLocation();
      return;
    }

    if (!userLocationFetched || userLocationLoading) return;

    const lat = Number(userLocationRow?.user_lat);
    const lng = Number(userLocationRow?.user_lng);
    const hasSavedLocation = Number.isFinite(lat) && Number.isFinite(lng);

    if (hasSavedLocation) {
      setUserLocation([lat, lng]);
      return;
    }

    fallbackToSystemLocation();
  }, [currentUserId, userLocationFetched, userLocationLoading, userLocationRow]);

  useEffect(() => {
    if (!athleteDemographics) return;
    if (!age && athleteDemographics.age) setAge(String(athleteDemographics.age));
    if (!grade && athleteDemographics.grade) setGrade(String(athleteDemographics.grade));
  }, [athleteDemographics, age, grade]);

  useEffect(() => {
    const compute = () => setListHeight(Math.max(320, window.innerHeight - 260));
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 640) {
      setShowFiltersPanel(false);
    }
  }, []);

  // --- Logic ---
  const getDistanceFromLatLonInMiles = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3958.8;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  const getZoomForDistance = useCallback((miles) => {
    if (miles <= 5) return 12;
    if (miles <= 15) return 11;
    if (miles <= 30) return 10;
    if (miles <= 40) return 9;
    return 8;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!showMobileMap && window.innerWidth < 1024) return;

    const syncMapSize = () => map.invalidateSize();
    const timeoutId = setTimeout(syncMapSize, 150);
    return () => clearTimeout(timeoutId);
  }, [showMobileMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || hasInitializedMapView.current) return;
    if (!Array.isArray(userLocation) || userLocation.length !== 2) return;

    map.setView(userLocation, getZoomForDistance(50), { animate: false });
    hasInitializedMapView.current = true;
  }, [getZoomForDistance, userLocation]);

  const displayedPrograms = useMemo(() => {
    if (selectedSearchProgram) return [selectedSearchProgram];
    const {
      age: appliedAge,
      grade: appliedGrade,
      registrationFormatFilter: appliedRegistrationFormat,
      genderFilter: appliedGender,
    } = appliedAdvancedFilters;

    return programs
      .map((p) => {
        if (!p.latitude || !p.longitude) return { ...p, distance: null };
        const dist = getDistanceFromLatLonInMiles(
          userLocation[0],
          userLocation[1],
          p.latitude,
          p.longitude
        );
        return { ...p, distance: dist };
      })
      .filter((p) => {
        const matchSport = sport ? p.sport === sport : true;
        let matchProgramType = true;
        if (programType) {
          if (programType === "Club Program") {
            matchProgramType =
              p.program_type === "Club Program" ||
              p.program_type === "Club/AAU" ||
              p.program_type === "AAU" ||
              p.program_type?.includes("Club") ||
              p.program_type?.includes("AAU");
          } else {
            matchProgramType =
              p.program_type === programType ||
              p.program_type?.includes(programType);
          }
        }
        const matchDistance = p.distance != null ? p.distance <= distance : false;
        const registrationFormat = p.registration_format || "age";
        const offeredLevels = [
          ...(p.age_groups || []),
          ...(p.boys_ages_offered || []),
          ...(p.girls_ages_offered || []),
        ].map((v) => Number(v)).filter((v) => !Number.isNaN(v));

        let matchLevel = true;
        if (registrationFormat === "grade" && appliedGrade) {
          matchLevel = offeredLevels.length > 0 ? offeredLevels.includes(Number(appliedGrade)) : false;
        } else if (registrationFormat === "age" && appliedAge) {
          matchLevel = offeredLevels.length > 0 ? offeredLevels.includes(Number(appliedAge)) : false;
        }
        const matchRegistrationFormat = appliedRegistrationFormat ? registrationFormat === appliedRegistrationFormat : true;
        const hasBoys = (p.boys_ages_offered || []).length > 0;
        const hasGirls = (p.girls_ages_offered || []).length > 0;
        let programGender = "";
        if (hasBoys && hasGirls) programGender = "coed";
        else if (hasBoys) programGender = "boys";
        else if (hasGirls) programGender = "girls";
        const matchGender = appliedGender ? programGender === appliedGender : true;

        return matchSport && matchLevel && matchProgramType && matchDistance && matchRegistrationFormat && matchGender;
      })
      .sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
  }, [
    appliedAdvancedFilters,
    distance,
    getDistanceFromLatLonInMiles,
    programType,
    programs,
    selectedSearchProgram,
    sport,
    userLocation,
  ]);

  const validMarkers = useMemo(
    () => [
      {
        id: "user",
        latitude: userLocation[0],
        longitude: userLocation[1],
        isUser: true,
      },
      ...displayedPrograms.filter((p) => p.latitude && p.longitude),
    ],
    [displayedPrograms, userLocation]
  );

  const searchSuggestions = useMemo(() => {
    if (debouncedSearchQuery.length === 0) return [];
    const q = debouncedSearchQuery.toLowerCase();
    return programs.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 5);
  }, [debouncedSearchQuery, programs]);

  const sportOptions = useMemo(
    () => [...new Set((programs || []).map((p) => p.sport).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [programs]
  );

  const programTypeOptions = useMemo(
    () => ["College Program", "High School Program", "Middle School Program", "Club Program", "Little League", "Skills Trainer"],
    []
  );

  const activePrimaryFilterCount = useMemo(
    () => [sport, programType].filter(Boolean).length + (distance !== 50 ? 1 : 0),
    [distance, programType, sport]
  );

  const activeAdvancedFilterCount = useMemo(
    () =>
      [
        appliedAdvancedFilters.age,
        appliedAdvancedFilters.grade,
        appliedAdvancedFilters.registrationFormatFilter,
        appliedAdvancedFilters.genderFilter,
      ].filter(Boolean).length,
    [appliedAdvancedFilters]
  );

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setSport("");
    setProgramType("");
    setAge("");
    setGrade("");
    setRegistrationFormatFilter("");
    setGenderFilter("");
    setAppliedAdvancedFilters({
      age: "",
      grade: "",
      registrationFormatFilter: "",
      genderFilter: "",
    });
    setSelectedSearchProgram(null);
    setShowAdvancedFilters(false);
  }, []);

  const applyAdvancedFilters = useCallback(() => {
    setAppliedAdvancedFilters({
      age,
      grade,
      registrationFormatFilter,
      genderFilter,
    });
    setSelectedSearchProgram(null);
  }, [age, grade, registrationFormatFilter, genderFilter]);

  // --- Handlers ---
  const handleFollow = useCallback(
    async (programId) => {
      const activeUserId = currentUserId;
      if (!activeUserId) {
        alert("You must be logged in to follow a program");
        return;
      }
      const cacheKey = ["followed-programs", activeUserId];
      const previous = queryClient.getQueryData(cacheKey) || [];
      const isAlreadyFollowed = previous.some((p) => p.program_id === programId);

      const optimistic = isAlreadyFollowed
        ? previous.filter((p) => p.program_id !== programId)
        : [...previous, { program_id: programId }];

      queryClient.setQueryData(cacheKey, optimistic);

      const { error } = isAlreadyFollowed
        ? await supabase
            .from("user_program_follows")
            .delete()
            .eq("user_id", activeUserId)
            .eq("program_id", programId)
        : await supabase
            .from("user_program_follows")
            .insert([{ user_id: activeUserId, program_id: programId }]);

      if (error) {
        queryClient.setQueryData(cacheKey, previous);
      }
    },
    [currentUserId, queryClient]
  );

  const handleSearchSelect = useCallback(
    (program) => {
      let dist = null;
      if (program.latitude && program.longitude) {
        dist = getDistanceFromLatLonInMiles(
          userLocation[0],
          userLocation[1],
          program.latitude,
          program.longitude
        );
      }
      setSelectedSearchProgram({ ...program, distance: dist });
      setSearchQuery(program.name);
      setIsDropdownOpen(false);
      if (window.innerWidth < 1024) setShowMobileMap(false);
    },
    [getDistanceFromLatLonInMiles, userLocation]
  );

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, []);

  const renderProgramCard = useCallback(
    (prog) => {
      const isFollowed = followedPrograms.includes(prog.id);
      const tData = tryoutData[prog.id];
      const hasTryouts = tData && tData.count > 0;

      return (
        <div
          className={`bg-white p-4 rounded-xl border transition-all duration-200 w-full flex flex-col ${
            selectedSearchProgram?.id === prog.id
              ? "border-blue-500 ring-1 ring-blue-500 shadow-md"
              : "border-gray-100 hover:border-gray-300 hover:shadow-sm"
          }`}
        >
          <div className="flex gap-4 mb-4">
            <div className="w-16 h-16 flex-shrink-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
              {prog.logo_url ? (
                <img
                  src={prog.logo_url}
                  alt={prog.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
              ) : null}
              <span
                className="text-xl font-bold text-gray-400"
                style={{ display: prog.logo_url ? "none" : "block" }}
              >
                {prog.name.charAt(0)}
              </span>
            </div>

            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <div className="flex justify-between items-start">
                <h3 className="font-bold text-gray-900 truncate text-lg pr-2">
                  {prog.name}
                </h3>
                {prog.distance !== null && (
                  <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                    {prog.distance.toFixed(1)} mi
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 truncate">
                {prog.sport} • {prog.program_type}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-auto pt-2 border-t border-gray-50">
            <button
              onClick={() => handleFollow(prog.id)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors border ${
                isFollowed
                  ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
              }`}
            >
              {isFollowed ? "Followed" : "Follow"}
            </button>

            {prog.website_url && (
              <button
                onClick={() =>
                  window.open(
                    prog.website_url.startsWith("http")
                      ? prog.website_url
                      : `https://${prog.website_url}`,
                    "_blank"
                  )
                }
                className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 border border-gray-200 hover:bg-gray-50"
              >
                Website
              </button>
            )}

            {hasTryouts && (
              <div className="ml-auto flex flex-col items-end sm:items-center sm:flex-row gap-2">
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Next: {formatDate(tData.nextDate)}
                </span>
                <button
                  onClick={() => navigate(`/athlete/program/${prog.id}/tryouts`)}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 shadow-sm active:scale-95"
                >
                  Register
                </button>
              </div>
            )}

            <div className="ml-2 flex gap-1">
              {prog.boys_ages_offered?.length > 0 && (
                <span className="w-2 h-2 rounded-full bg-blue-400" title="Boys"></span>
              )}
              {prog.girls_ages_offered?.length > 0 && (
                <span className="w-2 h-2 rounded-full bg-pink-400" title="Girls"></span>
              )}
            </div>
          </div>
        </div>
      );
    },
    [followedPrograms, formatDate, handleFollow, navigate, selectedSearchProgram, tryoutData]
  );

  const itemData = useMemo(
    () => ({ programs: displayedPrograms, renderProgramCard }),
    [displayedPrograms, renderProgramCard]
  );

  const Row = ({ index, style, data }) => {
    const prog = data.programs[index];
    if (!prog) return null;
    return (
      <div style={style} className="px-4 py-1">
        {data.renderProgramCard(prog)}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)] md:h-[calc(100vh-3rem)] w-full overflow-hidden relative bg-gray-50">

      {/* --- TOP HEADER --- */}
      <div className="bg-white border-b border-gray-200 z-[1001] shadow-sm flex-shrink-0">
        <div className="p-4 w-full">
          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition"
              placeholder="Search programs..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setIsDropdownOpen(true); if (!e.target.value) setSelectedSearchProgram(null); }}
              onFocus={() => setIsDropdownOpen(true)}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); setSelectedSearchProgram(null); }} className="absolute right-2 top-2 text-gray-400 p-1">
                <X size={14} />
              </button>
            )}
            {/* Dropdown */}
            {isDropdownOpen && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50">
                {searchSuggestions.map((p) => (
                  <div key={p.id} onClick={() => handleSearchSelect(p)} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 border-gray-100">
                    <div className="w-8 h-8 flex-shrink-0 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-500 overflow-hidden">
                      {p.logo_url ? <img src={p.logo_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : p.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{p.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 space-y-3">
            <button
              onClick={() => setShowFiltersPanel((prev) => !prev)}
              className="w-full flex items-center justify-between gap-3 text-left"
            >
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  Filter Programs
                </div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {activePrimaryFilterCount + activeAdvancedFilterCount > 0
                    ? `${activePrimaryFilterCount + activeAdvancedFilterCount} active filter${activePrimaryFilterCount + activeAdvancedFilterCount > 1 ? "s" : ""}`
                    : "Sport, type, distance, and optional advanced filters"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {activeAdvancedFilterCount > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold bg-blue-100 text-blue-700">
                    Advanced: {activeAdvancedFilterCount}
                  </span>
                )}
                <span className="text-gray-500">
                  {showFiltersPanel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </span>
              </div>
            </button>

            {showFiltersPanel ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <select
                    value={sport}
                    onChange={(e) => { setSport(e.target.value); setSelectedSearchProgram(null); }}
                    className="h-10 border border-gray-200 bg-white px-2 rounded-lg text-sm w-full outline-none focus:border-blue-500"
                  >
                    <option value="">All Sports</option>
                    {sportOptions.map((sportOption) => (
                      <option key={sportOption} value={sportOption}>{sportOption}</option>
                    ))}
                  </select>

                  <select
                    value={programType}
                    onChange={(e) => { setProgramType(e.target.value); setSelectedSearchProgram(null); }}
                    className="h-10 border border-gray-200 bg-white px-2 rounded-lg text-sm w-full outline-none focus:border-blue-500"
                  >
                    <option value="">All Types</option>
                    {programTypeOptions.map((typeOption) => (
                      <option key={typeOption} value={typeOption}>
                        {typeOption === "Club Program" ? "AAU/Club" : typeOption.replace(" Program", "")}
                      </option>
                    ))}
                  </select>

                  <select
                    value={distance}
                    onChange={(e) => { setDistance(Number(e.target.value)); setSelectedSearchProgram(null); }}
                    className="h-10 border border-gray-200 bg-white px-2 rounded-lg text-sm w-full outline-none focus:border-blue-500"
                  >
                    {[5, 10, 15, 20, 25, 30, 40, 50].map((miles) => (
                      <option key={miles} value={miles}>{miles} miles</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-200">
                  <button
                    onClick={() => setShowAdvancedFilters((prev) => !prev)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                      showAdvancedFilters
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    <SlidersHorizontal size={14} />
                    {showAdvancedFilters ? "Hide Advanced" : "Show Advanced"}
                  </button>
                  <button
                    onClick={clearFilters}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 border border-gray-200 bg-white hover:bg-gray-100"
                  >
                    Reset Filters
                  </button>
                </div>

                {showAdvancedFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                    <select value={age} onChange={(e) => { setAge(e.target.value); setSelectedSearchProgram(null); }} className="h-10 border border-gray-200 bg-white px-2 rounded-lg text-sm w-full outline-none focus:border-blue-500">
                      <option value="">All Ages</option>
                      {Array.from({ length: 11 }, (_, i) => i + 8).map((n) => (
                        <option key={n} value={n}>{n}U</option>
                      ))}
                    </select>
                    <select value={grade} onChange={(e) => { setGrade(e.target.value); setSelectedSearchProgram(null); }} className="h-10 border border-gray-200 bg-white px-2 rounded-lg text-sm w-full outline-none focus:border-blue-500">
                      <option value="">All Grades</option>
                      {Array.from({ length: 9 }, (_, i) => i + 4).map((n) => (
                        <option key={n} value={n}>Grade {n}</option>
                      ))}
                    </select>
                    <select value={registrationFormatFilter} onChange={(e) => { setRegistrationFormatFilter(e.target.value); setSelectedSearchProgram(null); }} className="h-10 border border-gray-200 bg-white px-2 rounded-lg text-sm w-full outline-none focus:border-blue-500">
                      <option value="">All Formats</option>
                      <option value="age">Age-based</option>
                      <option value="grade">Grade-based</option>
                    </select>
                    <select value={genderFilter} onChange={(e) => { setGenderFilter(e.target.value); setSelectedSearchProgram(null); }} className="h-10 border border-gray-200 bg-white px-2 rounded-lg text-sm w-full outline-none focus:border-blue-500">
                      <option value="">All Genders</option>
                      <option value="boys">Boys</option>
                      <option value="girls">Girls</option>
                      <option value="coed">Coed</option>
                    </select>
                    <button onClick={applyAdvancedFilters} className="h-10 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700">
                      Search
                    </button>
                    <button onClick={() => setShowAdvancedFilters(false)} className="h-10 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-100">
                      Collapse
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
                {sport || "All Sports"} • {programType || "All Types"} • {distance} miles
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- CONTENT --- */}
      <div className="flex-1 min-h-0 flex overflow-hidden relative">

        {/* 1. LEFT PANE (LIST) */}
        <div className={`${showMobileMap ? 'hidden' : 'block'} w-full lg:block lg:w-1/2 h-full overflow-y-auto overscroll-contain bg-gray-50 custom-scrollbar pb-[calc(7rem+var(--pwa-bottom-inset))] lg:pb-0`}>
          <div className="p-4">
            <div className="flex justify-between items-end px-1 mb-2">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Results</h2>
              <span className="text-xs text-gray-500 font-medium">{displayedPrograms.length} found</span>
            </div>

            {displayedPrograms.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-sm">No programs found within {distance} miles.</p>
                <button onClick={clearFilters} className="text-blue-500 text-xs font-bold mt-2 hover:underline">RESET FILTERS</button>
              </div>
            ) : (
              displayedPrograms.length > 40 ? (
                <VirtualList
                  height={listHeight}
                  itemCount={displayedPrograms.length}
                  itemSize={220}
                  width="100%"
                  itemData={itemData}
                >
                  {Row}
                </VirtualList>
              ) : (
                <div className="space-y-3">
                  {displayedPrograms.map((prog) => (
                    <div key={prog.id}>{renderProgramCard(prog)}</div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>

        {/* 2. RIGHT PANE (MAP) */}
        <div className={`${showMobileMap ? 'block' : 'hidden'} lg:block flex-1 h-full w-full relative bg-gray-100 z-0`}>
          {userLocation && (
            <MapContainer
              center={userLocation}
              zoom={getZoomForDistance(50)}
              style={{ height: "100%", width: "100%" }}
              whenReady={(event) => {
                mapRef.current = event.target;
              }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              {validMarkers.map((m) => (
                <Marker key={m.id} position={[m.latitude, m.longitude]} icon={new L.Icon.Default()}>
                  <Popup>
                    {m.isUser ? (
                      <div className="font-bold text-center">You</div>
                    ) : (
                      <div className="p-1 min-w-[200px]">
                        {/* --- Popup Design with Logo --- */}
                        <div className="flex gap-3 items-center mb-3 border-b border-gray-100 pb-2">
                          <div className="w-10 h-10 bg-white rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center border border-gray-200">
                             {m.logo_url ? <img src={m.logo_url} className="w-full h-full object-contain" alt="" loading="lazy" /> : <ImageIcon size={16} className="text-gray-400"/>}
                          </div>
                          <div>
                             <strong className="block text-sm text-gray-900 leading-tight">{m.name}</strong>
                             <span className="text-xs text-gray-500">{m.sport}</span>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <button onClick={() => navigate(`/athlete/program/${m.id}/tryouts`)} className="w-full bg-white border border-gray-300 text-gray-700 text-xs py-1.5 rounded hover:bg-gray-50 font-medium">
                            View Profile
                          </button>
                          {tryoutData[m.id]?.count > 0 && (
                            <button onClick={() => navigate(`/athlete/program/${m.id}/tryouts`)} className="w-full bg-blue-600 text-white text-xs py-1.5 rounded hover:bg-blue-700 font-bold">
                              Register for Tryout
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </Popup>
                </Marker>
              ))}
              <FitBounds markers={validMarkers} searchRadius={distance} />
            </MapContainer>
          )}
        </div>

        {/* Mobile Toggle */}
        <div className="lg:hidden absolute bottom-[calc(5rem+var(--pwa-bottom-inset))] right-4 z-[1000]">
          <button onClick={() => setShowMobileMap(!showMobileMap)} className="flex items-center gap-2 bg-slate-900/95 backdrop-blur-sm text-white px-5 py-3 rounded-full shadow-xl font-bold border border-white/20">
            {showMobileMap ? <><ListIcon size={18} /> List</> : <><MapIcon size={18} /> Map</>}
          </button>
        </div>

      </div>
    </div>
  );
}
